import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, limit, serverTimestamp, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, log } from '../utils/logger';
import { useAuth } from './AuthContext';

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  receiverId: string;
  content: string;
  type: 'text' | 'image' | 'voice' | 'location' | 'system';
  timestamp: Date;
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  metadata?: {
    imageUrl?: string;
    fileName?: string;
    fileSize?: number;
    coordinates?: { lat: number; lng: number };
  };
}

export interface Conversation {
  id: string;
  participants: string[];
  lastActivity: Date;
  unreadCount: number;
  typingUsers: string[];
  isOnline: boolean;
  matchType?: {
    theirChoice: 'fuck' | 'marry';
    myChoice: 'fuck' | 'marry';
  };
  lastMessage?: Message;
}

export interface TypingEvent {
  conversationId: string;
  userId: string;
  isTyping: boolean;
}

export interface ChatContextType {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  isConnected: boolean;
  isReconnecting: boolean;
  totalUnreadCount: number;
  sendMessage: (conversationId: string, content: string, type?: Message['type']) => Promise<boolean>;
  markAsRead: (conversationId: string) => Promise<void>;
  createConversation: (otherUserId: string, matchType?: any) => Promise<string | null>;
  loadMessages: (conversationId: string) => void;
  setTyping: (conversationId: string, isTyping: boolean) => void;
  getConversation: (conversationId: string) => Conversation | undefined;
  deleteConversation: (conversationId: string) => Promise<void>;
  blockUser: (userId: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const currentUserId = user?.uid || '';
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [isConnected, setIsConnected] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Calculate total unread count across all conversations
  const totalUnreadCount = conversations.reduce((total, conv) => total + conv.unreadCount, 0);

  const typingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const heartbeatIntervalRef = useRef<NodeJS.Timeout>();
  const messageUnsubscribes = useRef<Record<string, () => void>>({});

  // Firebase real-time listeners
  useEffect(() => {
    if (!currentUserId) return;

    const setupFirebaseListeners = () => {
      // Listen to conversations where current user is a participant
      const conversationsRef = collection(db, 'conversations');
      const conversationsQuery = query(
        conversationsRef,
        where('participants', 'array-contains', currentUserId),
        orderBy('lastActivity', 'desc'),
        limit(50)
      );

      const unsubscribeConversations = onSnapshot(conversationsQuery, (snapshot) => {
        const conversations: Conversation[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          conversations.push({
            id: doc.id,
            participants: data.participants,
            lastActivity: data.lastActivity?.toDate() || new Date(),
            unreadCount: data.unreadCounts?.[currentUserId] || 0,
            typingUsers: data.typingUsers || [],
            isOnline: data.participantStatus?.[getOtherParticipant(data.participants, currentUserId)]?.isOnline || false,
            matchType: data.matchType,
            lastMessage: data.lastMessage ? {
              ...data.lastMessage,
              timestamp: data.lastMessage.timestamp?.toDate() || new Date()
            } : undefined
          });
        });
        setConversations(conversations);
      }, (error) => {
        logError('Error fetching conversations:', error);
        setIsConnected(false);
      });

      return unsubscribeConversations;
    };

    const unsubscribe = setupFirebaseListeners();
    
    return () => {
      if (unsubscribe) unsubscribe();
      Object.values(typingTimeoutRef.current).forEach(clearTimeout);
      Object.values(messageUnsubscribes.current).forEach(unsubscribe => unsubscribe());
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    };
  }, [currentUserId]);

  // Helper function to get the other participant
  const getOtherParticipant = (participants: string[], currentUserId: string): string => {
    return participants.find(p => p !== currentUserId) || '';
  };

  // Create a new conversation between two users
  const createConversation = async (otherUserId: string, matchType?: any): Promise<string | null> => {
    try {
      // Check if conversation already exists
      const existingConvQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', currentUserId)
      );
      
      const snapshot = await getDocs(existingConvQuery);
      let existingConv = null;
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.participants.includes(otherUserId)) {
          existingConv = doc.id;
        }
      });
      
      if (existingConv) {
        return existingConv;
      }

      const conversationData = {
        participants: [currentUserId, otherUserId],
        lastActivity: serverTimestamp(),
        unreadCounts: {
          [currentUserId]: 0,
          [otherUserId]: 0
        },
        typingUsers: [],
        participantStatus: {
          [currentUserId]: { isOnline: true },
          [otherUserId]: { isOnline: false }
        },
        matchType: matchType || null,
        lastMessage: null,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'conversations'), conversationData);
      log('Created new conversation:', docRef.id);
      return docRef.id;
    } catch (error) {
      logError('Error creating conversation:', error);
      return null;
    }
  };

  // Load messages for a specific conversation
  const loadMessages = (conversationId: string) => {
    if (messages[conversationId] || messageUnsubscribes.current[conversationId]) {
      return; // Already loaded or loading
    }

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const messagesQuery = query(
      messagesRef,
      orderBy('timestamp', 'asc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const conversationMessages: Message[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        conversationMessages.push({
          id: doc.id,
          conversationId,
          senderId: data.senderId,
          receiverId: data.receiverId,
          content: data.content,
          type: data.type || 'text',
          timestamp: data.timestamp?.toDate() || new Date(),
          status: data.status || 'sent',
          metadata: data.metadata
        });
      });
      
      setMessages(prev => ({
        ...prev,
        [conversationId]: conversationMessages
      }));
    }, (error) => {
      logError('Error loading messages for conversation:', conversationId, error);
    });

    messageUnsubscribes.current[conversationId] = unsubscribe;
  };

  // Send a message
  const sendMessage = async (conversationId: string, content: string, type: Message['type'] = 'text'): Promise<boolean> => {
    try {
      const otherParticipant = conversations.find(c => c.id === conversationId)?.participants.find(p => p !== currentUserId);
      if (!otherParticipant) {
        throw new Error('Could not find conversation or other participant');
      }

      const messageData = {
        conversationId,
        senderId: currentUserId,
        receiverId: otherParticipant,
        content,
        type,
        timestamp: serverTimestamp(),
        status: 'sent'
      };

      // Add message to subcollection
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);

      // Update conversation's last message and activity
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: {
          ...messageData,
          timestamp: serverTimestamp()
        },
        lastActivity: serverTimestamp(),
        [`unreadCounts.${otherParticipant}`]: 1 // Increment unread count for other user
      });

      return true;
    } catch (error) {
      logError('Error sending message:', error);
      return false;
    }
  };

  // Mark conversation as read
  const markAsRead = async (conversationId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`unreadCounts.${currentUserId}`]: 0
      });
    } catch (error) {
      logError('Error marking conversation as read:', error);
    }
  };

  // Set typing status
  const setTyping = async (conversationId: string, isTyping: boolean) => {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversation = conversations.find(c => c.id === conversationId);
      
      if (!conversation) return;

      let newTypingUsers = [...conversation.typingUsers];
      
      if (isTyping) {
        if (!newTypingUsers.includes(currentUserId)) {
          newTypingUsers.push(currentUserId);
        }
        
        // Clear typing after 3 seconds
        if (typingTimeoutRef.current[conversationId]) {
          clearTimeout(typingTimeoutRef.current[conversationId]);
        }
        
        typingTimeoutRef.current[conversationId] = setTimeout(() => {
          setTyping(conversationId, false);
        }, 3000);
      } else {
        newTypingUsers = newTypingUsers.filter(userId => userId !== currentUserId);
      }

      await updateDoc(conversationRef, {
        typingUsers: newTypingUsers
      });
    } catch (error) {
      logError('Error updating typing status:', error);
    }
  };

  // Get a specific conversation
  const getConversation = (conversationId: string): Conversation | undefined => {
    return conversations.find(c => c.id === conversationId);
  };

  // Delete a conversation
  const deleteConversation = async (conversationId: string): Promise<void> => {
    try {
      // Note: In production, you might want to soft-delete or archive instead
      // For now, we'll just remove it from the user's view
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`deletedFor.${currentUserId}`]: true
      });
    } catch (error) {
      logError('Error deleting conversation:', error);
    }
  };

  // Block a user
  const blockUser = async (userId: string): Promise<void> => {
    try {
      // Add to blocked users list
      await updateDoc(doc(db, 'users', currentUserId), {
        blockedUsers: [...(user?.blockedUsers || []), userId]
      });
      
      // Remove all conversations with this user
      const userConversations = conversations.filter(c => c.participants.includes(userId));
      for (const conversation of userConversations) {
        await deleteConversation(conversation.id);
      }
    } catch (error) {
      logError('Error blocking user:', error);
    }
  };

  const value: ChatContextType = {
    conversations,
    messages,
    isConnected,
    isReconnecting,
    totalUnreadCount,
    sendMessage,
    markAsRead,
    createConversation,
    loadMessages,
    setTyping,
    getConversation,
    deleteConversation,
    blockUser
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};